import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import fleetApi from '../../services/api.service';

/** L'aperçu n'a pas été confirmé sous 24 h : il faut redéposer le fichier. */
export const IMPORT_PREVIEW_EXPIRED = 'IMPORT_PREVIEW_EXPIRED';

/**
 * Import de masse de commandes manuelles, en deux temps.
 *
 * Le dépôt lit le fichier et rend un aperçu ligne à ligne sans rien créer ; la
 * confirmation crée les BL des lignes correctes. Entre les deux, rien n'existe
 * dans le puits — c'est ce qui permet de corriger son fichier et de recommencer
 * sans avoir à annuler quoi que ce soit.
 *
 * Les lignes en erreur ne bloquent pas la confirmation : les lignes correctes
 * sont créées, les autres restent à corriger dans un second fichier. C'est le
 * même parti pris que celui du flux ERP, où un lot partiellement intégré est
 * accepté et tracé plutôt que rejeté en bloc.
 *
 * @returns {object} - { preview, isUploading, isConfirming, uploadFile, confirmImport, reset, downloadTemplate }
 */
export default function useImportOrders() {
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  /**
   * Dépose un fichier et rend son aperçu. Aucun BL n'est créé.
   *
   * @param {File} file - Le classeur déposé
   * @returns {Promise<object|null>} - { intake_id, rows }, ou null
   */
  const uploadFile = useCallback(async (file) => {
    setIsUploading(true);

    const body = new FormData();
    body.append('file', file);

    try {
      const { data } = await fleetApi.post('/orders-pool/import', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setPreview(data);

      return data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Le fichier n'a pas pu être lu, vérifiez qu'il suit le modèle."
      );
      setPreview(null);

      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Confirme l'aperçu : les lignes correctes deviennent des BL.
   *
   * @param {number} intakeId - L'aperçu à confirmer
   * @returns {Promise<object|null>} - { created, status, rows }, ou null
   */
  const confirmImport = useCallback(async (intakeId) => {
    setIsConfirming(true);

    try {
      const { data } = await fleetApi.post(
        `/orders-pool/import/${intakeId}/confirm`
      );

      /* L'aperçu est consommé : le reconfirmer ne créerait rien de plus. */
      setPreview(null);

      toast.success(
        data.created > 1
          ? `${data.created} BL créés depuis le fichier.`
          : `${data.created} BL créé depuis le fichier.`
      );

      return data;
    } catch (error) {
      const code = error?.response?.data?.code;

      /*
          Un aperçu périmé n'est pas une erreur de l'opérateur : la règle des
          24 heures existe pour qu'on ne crée pas, des semaines plus tard, des
          commandes dont plus rien ne dit qu'elles sont encore à faire.

          Il est effacé plutôt que laissé à l'écran : la modale reste ouverte
          sur cet échec, et un aperçu que l'API refuse désormais n'offrirait
          qu'un bouton de confirmation qui redonne la même erreur.
      */
      if (code === IMPORT_PREVIEW_EXPIRED) {
        setPreview(null);
      }

      toast.error(
        code === IMPORT_PREVIEW_EXPIRED
          ? 'Cet aperçu date de plus de 24 heures : redéposez le fichier.'
          : error?.response?.data?.message ||
              "L'import n'a pas abouti, veuillez réessayer."
      );

      return null;
    } finally {
      setIsConfirming(false);
    }
  }, []);

  const reset = useCallback(() => setPreview(null), []);

  /**
   * Télécharge le modèle de fichier déclaré par le tenant.
   *
   * Il est généré par l'API depuis la même déclaration que celle qui sert à
   * relire le fichier déposé : un modèle qui ne correspondrait plus à ce que
   * l'import attend est le premier piège d'un import par fichier.
   */
  const downloadTemplate = useCallback(async () => {
    try {
      const response = await fleetApi.get('/orders-pool/import/template', {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.download = 'modele-import-commandes.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Le modèle n'a pas pu être téléchargé.");
    }
  }, []);

  return {
    preview,
    isUploading,
    isConfirming,
    uploadFile,
    confirmImport,
    reset,
    downloadTemplate,
  };
}
